import React, { useState, useEffect } from 'react';
import { productService } from '../api/productService';
import { categoryService } from '../api/categoryService';
import Table from '../components/Common/Table';
import Modal from '../components/Common/Modal';
import Pagination from '../components/Common/Pagination';
import SearchBar from '../components/Common/SearchBar';
import ProductForm from '../components/Products/ProductForm';
import '../components/Common/Common.css';
import { brandService } from '../api/brandService';

function Products() {
  const [products, setProducts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [brands, setBrands] = useState([]);
  const [loading, setLoading] = useState(true);
  const [currentPage, setCurrentPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [searchTerm, setSearchTerm] = useState('');
  const [categoryFilter, setCategoryFilter] = useState('');
  const [brandFilter, setBrandFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingProduct, setEditingProduct] = useState(null);

  const fetchProducts = async () => {
    try {
      setLoading(true);
      const params = {
        page: currentPage,
        search: searchTerm,
        per_page: 15,
      };
      if (categoryFilter) params.category_id = categoryFilter;
      if (statusFilter) params.is_active = statusFilter;

      const response = await productService.getProducts(params);
      setProducts(response.data);
      setLastPage(response.meta?.last_page || 1);
    } catch (error) {
      console.error('Error fetching products:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchCategories = async () => {
    try {
      const response = await categoryService.getCategories({ per_page: 100 });
      setCategories(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  const fetchBrands = async () => {
    try {
      const response = await brandService.getBrands({ per_page: 100 });
      setBrands(response.data || []);
    } catch (error) {
      console.error('Error fetching categories:', error);
    }
  };

  useEffect(() => {
    fetchCategories();
    fetchBrands();
  }, []);

  useEffect(() => {
    fetchProducts();
  }, [currentPage, searchTerm, categoryFilter, statusFilter]);

  const handleDelete = async (id) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce produit?')) {
      try {
        await productService.deleteProduct(id);
        fetchProducts();
      } catch (error) {
        console.error('Error deleting product:', error);
        alert('Échec de la suppression du produit');
      }
    }
  };

  const handleEdit = (product) => {
    setEditingProduct(product);
    setShowModal(true);
  };

  const handleCreate = () => {
    setEditingProduct(null);
    setShowModal(true);
  };

  const handleFormSuccess = () => {
    setShowModal(false);
    setEditingProduct(null);
    fetchProducts();
  };

  const columns = [
    { key: 'id', label: 'ID' },
    {
      key: 'main_image',
      label: 'Image',
      render: (val) => val ? (
        <img src={val} alt="Product" style={{ width: '60px', height: '60px', objectFit: 'cover', borderRadius: '4px' }} />
      ) : 'Aucune image'
    },
    { key: 'name', label: 'Nom' },
    { key: 'category', label: 'Catégorie', render: (val) => val?.name || 'N/A' },
    { key: 'brand', label: 'Marque', render: (val) => val?.name || 'N/A' },
    {
      key: 'price',
      label: 'Prix',
      render: (val, row) => (
        <div>
          <div>{parseFloat(val).toFixed(2)} DH</div>
          {row.fake_original_price && (
            <small style={{ textDecoration: 'line-through', color: '#a0aec0' }}>
              {parseFloat(row.fake_original_price).toFixed(2)} DH
            </small>
          )}
        </div>
      )
    },
    { key: 'stock_quantity', label: 'Stock' },
    {
      key: 'media',
      label: 'Média',
      render: (val) => val?.length || 0
    },
    {
      key: 'is_best_seller',
      label: 'Badges',
      render: (val, row) => (
        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
          {row.is_best_seller && <span className="badge badge-warning">Meilleure vente</span>}
          {row.is_best_price && <span className="badge badge-info">Meilleur prix</span>}
        </div>
      ),
    },
    {
      key: 'is_active',
      label: 'Statut',
      render: (val) => (
        <span className={`badge ${val ? 'badge-success' : 'badge-danger'}`}>
          {val ? 'Actif' : 'Inactif'}
        </span>
      ),
    },
  ];

  return (
    <div>
      <div className="page-header">
        <h1>Produits</h1>
        <button onClick={handleCreate} className="btn btn-primary">
          Ajouter un produit
        </button>
      </div>

      <div className="filters">
        <div className="filters-grid">
          <SearchBar
            value={searchTerm}
            onChange={setSearchTerm}
            placeholder="Rechercher des produits par nom, SKU, code-barres..."
            onClear={() => setCurrentPage(1)}
          />
          <div className="form-group">
            <label>Filtrer par catégorie</label>
            <select value={categoryFilter} onChange={(e) => setCategoryFilter(e.target.value)}>
              <option value="">Toutes les catégories</option>
              {categories.map((cat) => (
                <option key={cat.id} value={cat.id}>{cat.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Filtrer par marque</label>
            <select value={brandFilter} onChange={(e) => setBrandFilter(e.target.value)}>
              <option value="">Toutes les marques</option>
              {brands.map((brand) => (
                <option key={brand.id} value={brand.id}>{brand.name}</option>
              ))}
            </select>
          </div>
          <div className="form-group">
            <label>Filtrer par statut</label>
            <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
              <option value="">Tous les statuts</option>
              <option value="1">Actif</option>
              <option value="0">Inactif</option>
            </select>
          </div>
        </div>
      </div>

      <Table
        columns={columns}
        data={products}
        loading={loading}
        onEdit={handleEdit}
        onDelete={handleDelete}
      />

      <Pagination
        currentPage={currentPage}
        lastPage={lastPage}
        onPageChange={setCurrentPage}
      />

      <Modal
        isOpen={showModal}
        onClose={() => setShowModal(false)}
        title={editingProduct ? 'Modifier le produit' : 'Ajouter un produit'}
        size="large"
      >
        <ProductForm
          product={editingProduct}
          onSuccess={handleFormSuccess}
          onCancel={() => setShowModal(false)}
        />
      </Modal>
    </div>
  );
}

export default Products;
